import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Button, Card, CardHeader, Monitor01, Phone01, Spinner, Tabs, Toggle } from "@opendate-app/setlist";

import { useBroadcastEditContext } from "../BroadcastEditContainer";
import SaveAsBlueprintModal from "../SaveAsBlueprintModal";
import { heatmapKey, paintLinkHeatmap } from "../heatmapUtils";
import { toastError, toastSuccess } from "../../../../shared/toastHelper";
import { formatCount, formatPercent, prettyUrl } from "./reportMath";

const styles = {
  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 320px",
    gap: 16,
    alignItems: "start",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginBottom: 12,
  },
  legend: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: "var(--sl-text-tertiary)",
  },
  scale: {
    width: 120,
    height: 10,
    borderRadius: 5,
    background: "linear-gradient(90deg, hsl(140,85%,48%), hsl(70,85%,48%), hsl(0,85%,48%))",
  },
  canvas: {
    display: "flex",
    justifyContent: "center",
    padding: 16,
    background: "var(--sl-bg-tertiary)",
    borderRadius: "var(--sl-radius-xl)",
  },
  frame: (width) => ({
    width,
    maxWidth: "100%",
    height: "70vh",
    border: "none",
    background: "#FFFFFF",
  }),
  linkRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 8,
    padding: "8px 0",
    fontSize: 14,
    borderTop: "1px solid var(--sl-border-secondary)",
  },
  muted: {
    fontSize: 13,
    color: "var(--sl-text-tertiary)",
  },
};

const ReportEmail = ({ heatmapOn, setHeatmapOn, onDuplicate }) => {
  const {
    csrfToken,
    team,
    broadcast,
    template,
    sampleData,
    beefreeTemplate,
    venueOwnership,
    overviewLinkClicks,
  } = useBroadcastEditContext();

  const [html, setHtml] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const iframeRef = useRef(null);

  const links = overviewLinkClicks || [];
  const totalClicks = links.reduce((sum, link) => sum + link.clicks, 0);
  const maxClicks = useMemo(() => links.reduce((max, link) => Math.max(max, link.clicks), 0), [links]);
  const stats = useMemo(() => new Map(links.map((link) => [heatmapKey(link.url), link])), [links]);

  useEffect(() => {
    let current = true;
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.post(`/teams/${team.id}/beefree_templates/${beefreeTemplate.id}/render_liquid_html`, {
      beefree_template: { template, sample_data: sampleData, venue_ownership_id: venueOwnership.id },
    })
      .then(({ data }) => {
        if (current) setHtml(data.parsedText || "");
      })
      .catch(() => {
        if (!current) return;
        setHtml("");
        toastError("Could not load the email, please try again");
      });

    return () => {
      current = false;
    };
  }, [beefreeTemplate.id, template, sampleData]);

  const blueprintError = (error, fallback) => toastError((error.response?.data?.errors || [fallback]).join(", "));

  const createTeamTemplate = (name, templateData, callback) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.post(`/teams/${team.id}/beefree_templates.json`, { beefree_template: { name, template: templateData } })
      .then(({ data }) => {
        toastSuccess("Blueprint created successfully");
        callback(data);
      })
      .catch((error) => {
        blueprintError(error, "Could not create blueprint");
        callback(null);
      });
  };

  const updateTeamTemplate = (blueprintId, templateData, callback) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.put(`/teams/${team.id}/beefree_templates/${blueprintId}.json`, { beefree_template: { template: templateData } })
      .then(({ data }) => {
        toastSuccess("Blueprint updated successfully");
        callback(data);
      })
      .catch((error) => {
        blueprintError(error, "Could not update blueprint");
        callback(null);
      });
  };

  const paint = () => {
    if (heatmapOn) paintLinkHeatmap(iframeRef.current?.contentDocument, stats, maxClicks);
  };

  return (
    <div style={styles.layout}>
      <div>
        <div style={styles.toolbar}>
          <Toggle label="Click heatmap" checked={heatmapOn} onChange={(event) => setHeatmapOn(event.target.checked)} />
          {heatmapOn && (
            <span style={styles.legend}>Fewer<span style={styles.scale} />More</span>
          )}
          <span style={{ flex: 1 }} />
          <Tabs
            variant="button-border"
            size="sm"
            collapseBelow={0}
            aria-label="Preview size"
            value={device}
            onChange={setDevice}
            items={[
              { value: "desktop", label: <><Monitor01 width={16} height={16} /> Desktop</> },
              { value: "mobile", label: <><Phone01 width={16} height={16} /> Mobile</> },
            ]}
          />
        </div>
        <div style={styles.canvas}>
          {html === null ? (
            <Spinner size="md" aria-label="Loading email" />
          ) : (
            <iframe
              key={heatmapOn ? "heatmap" : "plain"}
              ref={iframeRef}
              title="Sent email"
              srcDoc={html}
              sandbox="allow-same-origin"
              onLoad={paint}
              style={styles.frame(device === "mobile" ? 375 : 600)}
            />
          )}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {heatmapOn && (
          <Card header={<CardHeader title="Clicks by link" supportingText={`${formatCount(totalClicks)} tracked clicks`} />}>
            {links.length === 0 ? (
              <div style={styles.muted}>No link clicks tracked yet.</div>
            ) : links.map((link) => (
              <div key={link.url} style={styles.linkRow}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={link.url}>{prettyUrl(link.url)}</span>
                <span>{formatCount(link.clicks)} <span style={styles.muted}>{formatPercent(link.clicks, totalClicks)}</span></span>
              </div>
            ))}
          </Card>
        )}

        <Card header={<CardHeader title="Reuse this email" />}>
          <p style={styles.muted}>Sent emails can't be edited. Start a new broadcast with this design.</p>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
            <Button variant="link-color" size="sm" onClick={onDuplicate}>Duplicate to a new broadcast</Button>
            <Button variant="link-color" size="sm" onClick={() => setSavingTemplate(true)}>Save as team template</Button>
          </div>
        </Card>
      </div>

      <SaveAsBlueprintModal
        isOpen={savingTemplate}
        onClose={() => setSavingTemplate(false)}
        template={template}
        copiedFromBlueprint={broadcast.beefree_template?.copied_from_blueprint}
        teamId={team.id}
        createBlueprint={createTeamTemplate}
        updateBlueprint={updateTeamTemplate}
      />
    </div>
  );
};

export default ReportEmail;
